/**
 * @author       julian@zumper.com (Julian Napolitan)
 * @copyright    Copyright (c) 2019, Zumper
 * @description  analytics thunks
 */

import type { AutocompleteApiResult } from '@zcode/bizos'
import { trackGAEvent } from '@zcode/nr-utils'
import type { Dispatch, SetStateAction } from 'react'
import {
  AUTOCOMPLETE_CLICK,
  AUTOCOMPLETE_EXIT,
  AUTOCOMPLETE_FAILURE,
  AUTOCOMPLETE_HISTORICAL,
  AUTOCOMPLETE_KEYSTROKES,
  AUTOCOMPLETE_LENGTH,
  AUTOCOMPLETE_LOCATION,
  AUTOCOMPLETE_RETURNED,
  AUTOCOMPLETE_SUCCESS,
  AUTOCOMPLETE_TYPE,
  AUTOCOMPLETE_USE,
} from '../../../../../shared/constants/analytics'
import type { AutocompleteOrigin } from '../../constants'
import { getRawPreviousSearches } from '../../helpers/getPreviousSearches'

const trackAutocompleteAnalyticEvent = (
  category:
    | typeof AUTOCOMPLETE_FAILURE
    | typeof AUTOCOMPLETE_HISTORICAL
    | typeof AUTOCOMPLETE_SUCCESS
    | typeof AUTOCOMPLETE_TYPE
    | typeof AUTOCOMPLETE_USE,
  action:
    | typeof AUTOCOMPLETE_CLICK
    | typeof AUTOCOMPLETE_EXIT
    | typeof AUTOCOMPLETE_KEYSTROKES
    | typeof AUTOCOMPLETE_LENGTH
    | typeof AUTOCOMPLETE_LOCATION
    | typeof AUTOCOMPLETE_RETURNED,
  origin: AutocompleteOrigin | undefined,
  value?: number | `${number} | ${number}` | AutocompleteApiResult['type']
) => {
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  trackGAEvent(`${category} - ${origin}`, action, value)
  trackGAEvent(`${category} - All`, action, value)
}

const trackAutocompleteAnalytics = (
  category:
    | typeof AUTOCOMPLETE_FAILURE
    | typeof AUTOCOMPLETE_HISTORICAL
    | typeof AUTOCOMPLETE_SUCCESS
    | typeof AUTOCOMPLETE_TYPE
    | typeof AUTOCOMPLETE_USE,
  length: number,
  keystrokes: number,
  origin: AutocompleteOrigin | undefined,
  returned: number
) => {
  trackAutocompleteAnalyticEvent(category, AUTOCOMPLETE_KEYSTROKES, origin, keystrokes)
  trackAutocompleteAnalyticEvent(category, AUTOCOMPLETE_LENGTH, origin, length)
  trackAutocompleteAnalyticEvent(category, AUTOCOMPLETE_RETURNED, origin, returned)
}

export const trackExitClick = ({
  origin,
  keystrokes,
  predictionResults,
  searchTerm,
  setKeystrokes,
}: {
  origin: AutocompleteOrigin | undefined
  keystrokes: number
  predictionResults: readonly AutocompleteApiResult[] | null
  searchTerm: string
  setKeystrokes: Dispatch<SetStateAction<number>>
}) => {
  const returned = predictionResults?.length || 0
  const length = searchTerm?.length || 0
  trackAutocompleteAnalytics(AUTOCOMPLETE_FAILURE, length, keystrokes, origin, returned)
  trackAutocompleteAnalyticEvent(AUTOCOMPLETE_USE, AUTOCOMPLETE_EXIT, origin)
  setKeystrokes(0)
}

export const trackResultClick = ({
  index,
  keystrokes,
  origin,
  predictionResults,
  result,
  searchTerm,
}: {
  index: number
  keystrokes: number
  origin: AutocompleteOrigin | undefined
  predictionResults: readonly AutocompleteApiResult[] | null
  result: AutocompleteApiResult
  searchTerm: string
}) => {
  const prevSearches = getRawPreviousSearches()
  const length = searchTerm?.length
  const { type } = result
  if (predictionResults?.length) {
    const returned = predictionResults.length
    const selected = index + 1
    const clickVal = `${selected} | ${returned}` as const
    trackAutocompleteAnalyticEvent(AUTOCOMPLETE_USE, AUTOCOMPLETE_CLICK, origin, clickVal)
    trackAutocompleteAnalyticEvent(AUTOCOMPLETE_TYPE, AUTOCOMPLETE_LOCATION, origin, type)
    trackAutocompleteAnalytics(AUTOCOMPLETE_SUCCESS, length, keystrokes, origin, returned)
  } else if (prevSearches?.length) {
    const returned = prevSearches.length
    const selected = index
    const clickVal = `${selected} | ${returned}` as const
    trackAutocompleteAnalyticEvent(AUTOCOMPLETE_USE, AUTOCOMPLETE_CLICK, origin, clickVal)
    trackAutocompleteAnalyticEvent(AUTOCOMPLETE_TYPE, AUTOCOMPLETE_LOCATION, origin, type)
    trackAutocompleteAnalytics(AUTOCOMPLETE_HISTORICAL, length, keystrokes, origin, returned)
  }
}
